import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import "./CustomerSelectPage.css";

function publicSelectApi() {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return API;
  return `${window.location.origin}/api`;
}

function evalShowWhen(rule, answers) {
  if (!rule) return true;
  if (rule.all) return rule.all.every((part) => evalShowWhen(part, answers));
  if (rule.any) return rule.any.some((part) => evalShowWhen(part, answers));
  if (rule.answered) {
    const row = answers[rule.answered] || {};
    return row.status === "answered" && !row.unknown;
  }
  const tokens = (qid) => {
    const row = answers[qid] || {};
    if (row.unknown || row.status === "unknown") return ["unknown"];
    if (row.value) return [String(row.value)];
    return (row.values || []).map(String);
  };
  if (rule.eq) return tokens(rule.eq.question).includes(rule.eq.value);
  if (rule.neq) return !tokens(rule.neq.question).includes(rule.neq.value);
  if (rule.in) return tokens(rule.in.question).some((t) => (rule.in.values || []).includes(t));
  if (rule.includes) return tokens(rule.includes.question).includes(rule.includes.value);
  return true;
}

function emptyRow(qid) {
  return { question_id: qid, status: "empty", unknown: false, value: "", values: [], text: "", date: "", custom_request: "" };
}

function pruneHidden(answers, questions) {
  const next = { ...answers };
  let changed = true;
  let loops = 0;
  while (changed && loops < 6) {
    changed = false;
    loops += 1;
    for (const q of questions || []) {
      if (evalShowWhen(q.show_when, next)) continue;
      const row = next[q.id];
      if (!row) continue;
      const has = row.unknown || row.status === "answered" || row.status === "unknown" || row.value || (row.values || []).length || row.text || row.date || row.custom_request;
      if (!has) continue;
      next[q.id] = emptyRow(q.id);
      changed = true;
    }
  }
  return next;
}

function OptionCard({ item, selected, onClick, testId, mark }) {
  return (
    <button type="button" className={`csel-option${selected ? " is-on" : ""}`} onClick={onClick} data-testid={testId}>
      <span className="csel-option-mark" aria-hidden>{selected ? "☑" : (mark || "☐")}</span>
      <span className="csel-option-copy">
        <strong>{item.customer_label}</strong>
        {item.customer_description ? <em>{item.customer_description}</em> : null}
      </span>
    </button>
  );
}

function QuestionCard({ question, row, unknownKey, otherKey, onSingle, onMulti, onText, onDate, onCustom }) {
  const options = question.options || [];
  const isMulti = question.answer_type === "multi";
  const showOther = (row.values || []).includes(otherKey) || row.value === otherKey;
  return (
    <section className="csel-q" data-testid={`csel-q-${question.id}`}>
      <h3>{question.customer_label}</h3>
      {question.customer_description ? <p className="csel-help">{question.customer_description}</p> : null}
      {question.answer_type === "text" && (
        <textarea rows={4} value={row.text || ""} onChange={(e) => onText(question.id, e.target.value)} />
      )}
      {question.answer_type === "date" && (
        <input type="date" value={row.date || ""} onChange={(e) => onDate(question.id, e.target.value)} />
      )}
      {question.answer_type === "date_or_period" && (
        <>
          <div className="csel-options">
            {options.map((opt) => (
              <OptionCard
                key={opt.key}
                item={opt}
                selected={row.value === opt.key}
                testId={`csel-opt-${question.id}-${opt.key}`}
                onClick={() => onSingle(question.id, opt.key, unknownKey)}
              />
            ))}
          </div>
          {row.value === "date" ? (
            <input type="date" value={row.date || ""} onChange={(e) => onDate(question.id, e.target.value, "date")} />
          ) : null}
        </>
      )}
      {(question.answer_type === "single") && (
        <div className="csel-options">
          {options.map((opt) => (
            <OptionCard
              key={opt.key}
              item={opt}
              selected={row.value === opt.key}
              testId={`csel-opt-${question.id}-${opt.key}`}
              onClick={() => onSingle(question.id, opt.key, unknownKey)}
            />
          ))}
        </div>
      )}
      {isMulti && (
        <div className="csel-options">
          {options.map((opt) => (
            <OptionCard
              key={opt.key}
              item={opt}
              selected={(row.values || []).includes(opt.key)}
              testId={`csel-opt-${question.id}-${opt.key}`}
              onClick={() => onMulti(question.id, opt.key, unknownKey, otherKey)}
            />
          ))}
        </div>
      )}
      {(showOther || (question.allow_custom_request && showOther)) && (
        <textarea
          rows={3}
          placeholder="원하시는 기능을 적어 주세요. 이 화면에서 가격은 계산되지 않습니다."
          value={row.custom_request || ""}
          onChange={(e) => onCustom(question.id, e.target.value)}
        />
      )}
    </section>
  );
}

function summaryLines(steps, answers, unknownKey) {
  const out = [];
  const seen = new Set();
  for (const step of steps || []) {
    for (const q of step.questions || []) {
      if (seen.has(q.id)) continue;
      seen.add(q.id);
      const row = answers[q.id] || {};
      let text = "";
      if (row.unknown || row.status === "unknown" || row.value === unknownKey) text = "잘 모르겠어요 (추가 확인 필요)";
      else if (row.value) {
        const opt = (q.options || []).find((o) => o.key === row.value);
        text = opt?.customer_label || row.value;
      } else if ((row.values || []).length) {
        text = (row.values || []).map((key) => {
          const opt = (q.options || []).find((o) => o.key === key);
          return opt?.customer_label || key;
        }).join(", ");
      } else if (row.text) text = row.text;
      else if (row.date) text = row.date;
      if (text) out.push({ id: q.id, label: q.customer_label, text });
    }
  }
  return out;
}

export default function CustomerSelectPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [flow, setFlow] = useState("");
  const [step, setStep] = useState(0);
  const [contact, setContact] = useState({ company_name: "", contact_name: "", phone: "", email: "" });
  const [answers, setAnswers] = useState({});
  const [extraRequests, setExtraRequests] = useState({});
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const submitLock = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${publicSelectApi()}/public/consult-select/${token}`);
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.detail || "링크를 열 수 없습니다.");
        if (!cancelled) {
          setData(body);
          if (body.submitted) setDone(true);
          if (body.company_name) setContact((prev) => ({ ...prev, company_name: body.company_name }));
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "링크를 열 수 없습니다.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const guide = data?.guide || {};
  const unknownKey = guide.unknown_key || "unknown";
  const otherKey = guide.other_key || "other";
  const visibleSteps = useMemo(() => {
    const flowSteps = flow === "direct" ? (guide.direct_steps || []) : flow === "custom" ? (guide.custom_steps || []) : (guide.recommend_steps || []);
    return (flowSteps || []).map((item) => {
      const questions = (item.questions || []).filter((q) => evalShowWhen(q.show_when, answers));
      return { ...item, questions };
    }).filter((item) => item.id === "contact" || item.id === "review" || item.questions.length > 0 || item.extra_request);
  }, [flow, guide.direct_steps, guide.custom_steps, guide.recommend_steps, answers]);

  const current = visibleSteps[step] || visibleSteps[0] || { id: "contact", title: "기본정보", questions: [] };

  const guideQuestions = useMemo(() => {
    const steps = flow === "direct" ? (guide.direct_steps || []) : flow === "custom" ? (guide.custom_steps || []) : (guide.recommend_steps || []);
    return (steps || []).flatMap((item) => item.questions || []);
  }, [flow, guide.direct_steps, guide.custom_steps, guide.recommend_steps]);

  const setSingle = (qid, key, unk) => {
    setAnswers((prev) => pruneHidden({
      ...prev,
      [qid]: {
        ...emptyRow(qid),
        ...(prev[qid] || {}),
        value: key,
        values: [],
        unknown: key === unk,
        status: key === unk ? "unknown" : "answered",
      },
    }, guideQuestions));
  };
  const setMulti = (qid, key, unk, other) => {
    setAnswers((prev) => {
      const row = { ...emptyRow(qid), ...(prev[qid] || {}) };
      let values = [...(row.values || [])];
      if (key === unk) values = values.includes(unk) ? [] : [unk];
      else {
        values = values.filter((v) => v !== unk);
        values = values.includes(key) ? values.filter((v) => v !== key) : [...values, key];
      }
      const unknown = values.length === 1 && values[0] === unk;
      return pruneHidden({
        ...prev,
        [qid]: {
          ...row,
          values,
          value: "",
          unknown,
          status: unknown ? "unknown" : (values.length ? "answered" : "empty"),
          custom_request: key === other && !values.includes(other) ? "" : row.custom_request,
        },
      }, guideQuestions);
    });
  };
  const setText = (qid, text) => {
    setAnswers((prev) => ({
      ...prev,
      [qid]: { ...emptyRow(qid), ...(prev[qid] || {}), text, status: String(text || "").trim() ? "answered" : "empty" },
    }));
  };
  const setDate = (qid, date, period) => {
    setAnswers((prev) => ({
      ...prev,
      [qid]: {
        ...emptyRow(qid),
        ...(prev[qid] || {}),
        date,
        value: period || prev[qid]?.value || "date",
        status: date || period ? "answered" : "empty",
      },
    }));
  };
  const setCustom = (qid, text) => {
    setAnswers((prev) => ({ ...prev, [qid]: { ...emptyRow(qid), ...(prev[qid] || {}), custom_request: text } }));
  };

  const payloadBody = () => ({
    ...contact,
    flow_type: flow,
    answers: pruneHidden(answers, guideQuestions),
    extra_requests: extraRequests,
    extra_request: Object.values(extraRequests).filter(Boolean).join("\n"),
    notes: Object.values(extraRequests).filter(Boolean).join("\n"),
  });

  const loadPreview = async () => {
    try {
      const r = await fetch(`${publicSelectApi()}/public/consult-select/${token}/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadBody()),
      });
      const body = await r.json().catch(() => ({}));
      if (r.ok) setPreview(body);
    } catch {
      setPreview(null);
    }
  };

  const canProceed = () => {
    if (current.id === "contact") {
      return Boolean(contact.company_name.trim() || contact.contact_name.trim() || contact.phone.trim() || contact.email.trim());
    }
    return true;
  };

  const next = async () => {
    if (step < visibleSteps.length - 1) {
      const upcoming = visibleSteps[step + 1];
      if (upcoming?.id === "review") await loadPreview();
      setStep(step + 1);
    }
  };
  const prev = () => {
    if (step > 0) setStep(step - 1);
    else setFlow("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitLock.current || sending) return;
    submitLock.current = true;
    setSending(true);
    setError("");
    try {
      const r = await fetch(`${publicSelectApi()}/public/consult-select/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadBody()),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || "제출에 실패했습니다.");
      if (body.customer_summary) setPreview(body.customer_summary);
      setDone(true);
    } catch (err) {
      submitLock.current = false;
      setSending(false);
      setError(err.message || "제출에 실패했습니다.");
    }
  };

  const lines = useMemo(() => summaryLines(visibleSteps, answers, unknownKey), [visibleSteps, answers, unknownKey]);

  if (error && !data) {
    return <div className="csel"><div className="csel-card"><h1>요청 링크</h1><p>{error}</p></div></div>;
  }
  if (!data) return <div className="csel"><div className="csel-card">불러오는 중...</div></div>;
  if (done || data.submitted) {
    return (
      <div className="csel">
        <div className="csel-card" data-testid="csel-done">
          <p className="csel-brand">DSITESOLUTION</p>
          <h1>상담 요청이 전달되었습니다</h1>
          <p>입력해주신 내용을 담당자가 검토한 후 구축 범위와 견적을 안내드립니다.</p>
          <p className="csel-help">이 단계에서는 금액이 계산되거나 표시되지 않습니다.</p>
        </div>
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="csel">
        <div className="csel-card" data-testid="csel-entry">
          <p className="csel-brand">DSITESOLUTION</p>
          <h1>{data.company_name ? `${data.company_name} 상담 요청` : "필요한 내용을 알려 주세요"}</h1>
          <p className="csel-help">가격은 표시되지 않습니다. 몇 가지 질문에 답하시면 담당자가 범위를 정리해 드립니다.</p>
          <div className="csel-flows">
            {(guide.flows || []).map((item) => (
              <button
                key={item.id}
                type="button"
                className="csel-flow"
                data-testid={`csel-flow-${item.id}`}
                onClick={() => { setFlow(item.id); setStep(0); }}
              >
                <span className="csel-flow-kicker">{item.title}</span>
                <strong>{item.customer_label}</strong>
                <em>{item.customer_description}</em>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="csel">
      <form className="csel-card" onSubmit={submit} data-testid={`csel-flow-page-${flow}`}>
        <p className="csel-brand">DSITESOLUTION</p>
        <h1>{current.title}</h1>
        <p className="csel-help">가격은 표시되지 않습니다. 제출 후에도 견적은 확정되지 않습니다.</p>
        <div className="csel-steps" data-testid="csel-steps">
          {visibleSteps.map((s, i) => (
            <button key={s.id} type="button" className={i === step ? "is-on" : ""} onClick={() => setStep(i)}>
              {i + 1} {s.short || s.title}
            </button>
          ))}
        </div>
        {error ? <p className="csel-err">{error}</p> : null}

        {current.not_all_notice ? <p className="csel-notice" data-testid="csel-not-all">{current.not_all_notice}</p> : null}

        {current.id === "contact" && (
          <div className="csel-grid">
            <label>회사/상호<input value={contact.company_name} onChange={(e) => setContact({ ...contact, company_name: e.target.value })} autoComplete="organization" /></label>
            <label>담당자<input value={contact.contact_name} onChange={(e) => setContact({ ...contact, contact_name: e.target.value })} autoComplete="name" /></label>
            <label>연락처<input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} autoComplete="tel" /></label>
            <label>이메일<input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} autoComplete="email" /></label>
          </div>
        )}

        {current.id !== "contact" && current.id !== "review" && (current.questions || []).map((q) => (
          <QuestionCard
            key={q.id}
            question={q}
            row={answers[q.id] || emptyRow(q.id)}
            unknownKey={unknownKey}
            otherKey={otherKey}
            onSingle={setSingle}
            onMulti={setMulti}
            onText={setText}
            onDate={setDate}
            onCustom={setCustom}
          />
        ))}

        {current.extra_request ? (
          <label className="csel-field" data-testid={`csel-extra-${current.id}`}>
            <strong>{guide.extra_request_label || "목록에 없는 기능이 있어요"}</strong>
            <em>{guide.extra_request_help}</em>
            <textarea
              rows={3}
              value={extraRequests[current.id] || ""}
              onChange={(e) => setExtraRequests((prev) => ({ ...prev, [current.id]: e.target.value }))}
              placeholder="추가 요청 직접 입력"
            />
          </label>
        ) : null}

        {current.id === "review" && (
          <div className="csel-review" data-testid="csel-review">
            <h2>입력하신 내용을 확인해주세요.</h2>
            {preview?.headline ? <p className="csel-headline" data-testid="csel-result-headline">{preview.headline}</p> : null}
            {(preview?.confirmed || []).length ? (
              <div>
                <h3>주요 요청</h3>
                <ul>{preview.confirmed.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
            ) : null}
            {(preview?.additional_checks || []).length ? (
              <div>
                <h3>추가 확인이 필요한 내용</h3>
                <ul>{preview.additional_checks.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
            ) : null}
            {preview?.followup ? <p className="csel-help">{preview.followup}</p> : null}
            <div className="csel-summary">
              {lines.map((row) => (
                <div key={row.id} className="csel-summary-row">
                  <strong>{row.label}</strong>
                  <span>{row.text}</span>
                  <button type="button" className="csel-ghost csel-mini" onClick={() => {
                    const idx = visibleSteps.findIndex((s) => (s.questions || []).some((q) => q.id === row.id) || s.id === "contact");
                    if (idx >= 0) setStep(idx);
                  }}>수정</button>
                </div>
              ))}
              {Object.values(extraRequests).filter(Boolean).length ? (
                <div className="csel-summary-row">
                  <strong>기타 / 목록에 없는 기능</strong>
                  <span>{Object.values(extraRequests).filter(Boolean).join(" / ")}</span>
                </div>
              ) : null}
            </div>
          </div>
        )}

        <div className="csel-nav">
          <button type="button" className="csel-ghost" onClick={prev} data-testid="csel-back">이전</button>
          {current.id === "review" ? (
            <button type="submit" className="csel-submit" disabled={sending} data-testid="csel-submit">
              {sending ? "제출 중..." : "상담 요청 보내기"}
            </button>
          ) : (
            <button type="button" className="csel-submit" onClick={next} disabled={!canProceed()} data-testid="csel-next">다음</button>
          )}
        </div>
      </form>
    </div>
  );
}
